import {
  Workbox,
  WorkboxLifecycleEvent,
  WorkboxMessageEvent,
} from 'workbox-window';

import {APIState, APIStateStore} from '@stores/api-state.store';

/**
 * sessionStorage flag read by AppComponent to show the "app updated" snackbar
 * after the reload triggered by a service worker update.
 */
export const SW_UPDATED_STORAGE_KEY = 'pl-sw-updated';

export interface RegisterServiceWorkerOptions {
  /** Workbox instance for the app's service worker script. */
  workbox: Workbox;
  /** Store updated from the SW's offline/online status messages. */
  apiStateStore?: Pick<APIStateStore, 'setAPIState'> | null;
  /** Document whose visibility changes trigger an update check. */
  document: Pick<
    Document,
    'addEventListener' | 'removeEventListener' | 'visibilityState'
  >;
  /** Storage used to flag the post-update reload. */
  storage?: Pick<Storage, 'setItem'> | null;
  /** Reloads the page. */
  reload: () => void;
}

/**
 * Wires up the service worker lifecycle and registers it.
 *
 * - Reloads the page when a NEW service worker takes control of a page that
 *   was already controlled (`controlling` with `isUpdate`). Unlike
 *   `installed.isUpdate`, this is not limited to the first update found within
 *   60s of `register()` (workbox-window 7), so it also covers long-lived tabs
 *   and external updates. It fires after `clientsClaim()`, so the reload is
 *   answered by the new SW's precache.
 * - Checks for a service worker update whenever the tab becomes visible.
 * - Forwards the SW's offline/online status messages to the APIStateStore.
 *
 * @returns a function that removes the document listener (used by tests).
 */
export function registerServiceWorker(
  options: RegisterServiceWorkerOptions,
): () => void {
  const {workbox, apiStateStore, document, storage, reload} = options;

  workbox.addEventListener('controlling', (event: WorkboxLifecycleEvent) => {
    if (event.isUpdate) {
      storage?.setItem(SW_UPDATED_STORAGE_KEY, 'true');
      reload();
    }
  });

  workbox.addEventListener('message', (event: WorkboxMessageEvent) => {
    if (!apiStateStore) {
      return;
    }
    switch (event.data?.status) {
      case 'offline':
        apiStateStore.setAPIState(APIState.Offline);
        break;
      case 'online':
        apiStateStore.setAPIState(APIState.Default);
        break;
    }
  });

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      workbox.update().catch((err: unknown) => {
        console.debug('Service worker update check failed', err);
      });
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  workbox.register().catch((err: unknown) => {
    console.error('Failed to register service worker');
    console.error(err);
  });

  return () =>
    document.removeEventListener('visibilitychange', onVisibilityChange);
}
